import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, TextInput } from "@/components";
import { X, Plus, Upload } from "lucide-react";
import type { Brand, CreateBrandParams, UpdateBrandParams, BrandConfig } from "../interfaces";

const brandSchema = yup.object({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  imageUrls: yup.array().of(yup.string().url("Must be a valid URL")).optional(),
  videos: yup.array().of(yup.string().url("Must be a valid URL")).optional(),
  info: yup.array().of(yup.string()).optional(),
  faqs: yup
    .array()
    .of(
      yup.object({
        question: yup.string().required("Question is required"),
        answer: yup.string().required("Answer is required"),
      })
    )
    .optional(),
});

type BrandFormData = {
  title: string;
  description: string;
  imageUrls: string[];
  videos: string[];
  info: string[];
  faqs: {
    question: string;
    answer: string;
  }[];
};

interface BrandFormProps {
  brand?: Brand;
  onSubmit: (data: CreateBrandParams | UpdateBrandParams) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const BrandForm: React.FC<BrandFormProps> = ({
  brand,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<BrandFormData>({
    resolver: yupResolver(brandSchema) as any,
    defaultValues: brand
      ? {
          title: brand.title,
          description: brand.description,
          imageUrls: [],
          videos: brand.videos || [],
          info: brand.config?.product_or_service_info || [],
          faqs: brand.config?.faq || [],
        }
      : {
          title: "",
          description: "",
          imageUrls: [],
          videos: [],
          info: [],
          faqs: [],
        },
  });

  // FieldArrayPath excludes primitive arrays (string[]) - see react-hook-form#7622
  const {
    fields: infoFields,
    append: appendInfo,
    remove: removeInfo,
  } = useFieldArray({
    control,
    // @ts-expect-error FieldArrayPath excludes string[] - works at runtime
    name: "info",
  });

  const {
    fields: faqFields,
    append: appendFaq,
    remove: removeFaq,
  } = useFieldArray({
    control,
    name: "faqs",
  });

  // FieldArrayPath excludes primitive arrays (string[]) - see react-hook-form#7622
  const {
    fields: videoFields,
    append: appendVideo,
    remove: removeVideo,
  } = useFieldArray({
    control,
    // @ts-expect-error FieldArrayPath excludes string[] - works at runtime
    name: "videos",
  });

  useEffect(() => {
    if (brand?.images) {
      setImagePreviews(brand.images);
    }
  }, [brand]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 10) {
      alert("Maximum 10 images allowed");
      return;
    }
    setImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    setImagePreviews(newPreviews);
  };

  const onFormSubmit = async (data: BrandFormData) => {
    const config: BrandConfig = {
      product_or_service_info: (data.info || []).filter((item): item is string => typeof item === "string"),
      faq: data.faqs || [],
    };

    if (brand) {
      // Update brand
      await onSubmit({
        id: brand.id,
        title: data.title,
        description: data.description,
        images: imageFiles.length > 0 ? imageFiles : undefined,
        imageUrls: data.imageUrls && data.imageUrls.length > 0 ? data.imageUrls : undefined,
        videos: data.videos && data.videos.length > 0 ? data.videos : undefined,
        config,
      } as UpdateBrandParams);
    } else {
      // Create brand
      await onSubmit({
        title: data.title,
        description: data.description,
        images: imageFiles.length > 0 ? imageFiles : undefined,
        imageUrls: data.imageUrls && data.imageUrls.length > 0 ? data.imageUrls : undefined,
        videos: data.videos && data.videos.length > 0 ? data.videos : undefined,
        config,
      } as CreateBrandParams);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <TextInput
        label="Title"
        placeholder="Enter brand title"
        error={!!errors.title}
        helperText={errors.title?.message}
        {...register("title")}
      />

      <div>
        <label className="text-sm font-medium mb-2 block">Description</label>
        <textarea
          className="w-full min-h-[100px] px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="Enter brand description"
          {...register("description")}
        />
        {errors.description && (
          <p className="text-xs text-destructive mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Images</label>
        <div className="space-y-4">
          <div>
            <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-input rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="flex flex-col items-center">
                <Upload className="h-6 w-6 text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">
                  Click to upload images (max 10)
                </span>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </div>

          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-4 gap-4">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-2 block">
              Or add image URLs (one per line)
            </label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
              defaultValue={watch("imageUrls")?.join("\n") || ""}
              onChange={(e) => {
                const urls = e.target.value
                  .split("\n")
                  .map((url) => url.trim())
                  .filter((url) => url.length > 0);
                setValue("imageUrls", urls);
              }}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Videos</label>
        {videoFields.map((field, index) => (
          <div key={field.id} className="flex gap-2 mb-2">
            <TextInput
              placeholder="https://www.youtube.com/embed/xyz123"
              className="flex-1"
              {...register(`videos.${index}` as const)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeVideo(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => appendVideo("" as any)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Video URL
        </Button>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">Brand Info</label>
        {infoFields.map((field, index) => (
          <div key={field.id} className="flex gap-2 mb-2">
            <TextInput
              placeholder="Enter brand information"
              className="flex-1"
              {...register(`info.${index}` as const)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeInfo(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => appendInfo("" as any)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Info
        </Button>
      </div>

      <div>
        <label className="text-sm font-medium mb-2 block">FAQs</label>
        {faqFields.map((field, index) => (
          <div key={field.id} className="border border-input rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">FAQ {index + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeFaq(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <TextInput
              label="Question"
              placeholder="Enter question"
              className="mb-2"
              {...register(`faqs.${index}.question` as const)}
            />
            <div>
              <label className="text-sm font-medium mb-2 block">Answer</label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="Enter answer"
                {...register(`faqs.${index}.answer` as const)}
              />
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => appendFaq({ question: "", answer: "" })}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      <div className="flex gap-4 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : brand ? "Update Brand" : "Create Brand"}
        </Button>
      </div>
    </form>
  );
};
